import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useFacility, useUpdateFacility } from "@/hooks/use-facilities";
import { useInlineField } from "@/hooks/use-inline-field";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useFacilityDeviceSummary } from "@/hooks/use-facility-device-summary";
import { FacilityOverviewTab } from "@/components/facilities/facility-overview-tab";
import { DevicesTab } from "@/components/facilities/devices-tab";
import { FacilityMetaRow } from "@/components/facilities/facility-meta-row";
import { FacilityAccountsCard } from "@/components/facilities/facility-accounts-card";
import { FacilityStatusControl } from "@/components/facilities/facility-status-control";
import { ContactsCard } from "@/components/facilities/contacts-card";
import { EnforcementIcon, MailIcon } from "@/components/icons";
import {
  PageHeader,
  Breadcrumbs,
  Button,
  PageSkeleton,
  Tabs,
  EmptyState,
  InlineEditable,
  InlineTextEditor,
  type TabDef,
} from "@/components/ui";
import { FacilityTestingTab } from "@/components/facilities/facility-testing-tab";
import { FacilityComplianceTab } from "@/components/facilities/facility-compliance-tab";
import { FacilityHistoryTab } from "@/components/facilities/facility-history-tab";
import { FacilityTasksTab } from "@/components/facilities/facility-tasks-tab";
import { FacilityDocumentsTab } from "@/components/facilities/facility-documents-tab";
import { NotesCard } from "@/components/entity-notes/notes-card";
import { CreateViolationDrawer } from "@/components/violations/create-violation-drawer";

export type FacilityTab =
  | "overview"
  | "associations"
  | "devices"
  | "testing"
  | "compliance"
  | "tasks"
  | "documents"
  | "history";

const ALLOWED_TABS: FacilityTab[] = [
  "overview",
  "associations",
  "devices",
  "testing",
  "compliance",
  "tasks",
  "documents",
  "history",
];

interface FacilityDetailSearch {
  tab?: FacilityTab;
}

function parseTab(value: unknown): FacilityTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((t) => t === value);
}

export const Route = createFileRoute("/_authenticated/facilities/$facilityId/")({
  component: FacilityDetailPage,
  validateSearch: (search: Record<string, unknown>): FacilityDetailSearch => ({
    tab: parseTab(search.tab),
  }),
});

function FacilityDetailPage() {
  const { t } = useTranslation(["facilities", "cccp", "common", "notices", "violations"]);
  const navigate = useNavigate();
  const { facilityId } = Route.useParams();
  const { tab } = Route.useSearch();

  const orgId = useAppStore((s) => s.activeOrgId);
  // Facility-attribute surfaces (inline title + status, the Overview field set,
  // accounts/contacts) gate on facilities:update; each entity sub-tab gates on
  // its OWN entity.
  const { can } = usePermissions();
  const canUpdateFacility = can("facilities", "update");
  const canCreateDevice = can("device_installations", "create");

  const { data: facility, isLoading, isError } = useFacility(orgId, facilityId);
  useRecentPageTitle(facility?.name ?? undefined);
  const updateMutation = useUpdateFacility(orgId, facilityId);
  const patchField = useInlineField<components["schemas"]["UpdateFacilityRequest"]>(
    (body) => updateMutation.mutateAsync(body),
    { successKey: "facilities:updated" },
  );
  const summary = useFacilityDeviceSummary(facilityId);
  const [violationDrawerOpen, setViolationDrawerOpen] = useState(false);

  const activeTab: FacilityTab = tab ?? "overview";

  const setActiveTab = (next: FacilityTab) => {
    void navigate({
      to: "/facilities/$facilityId",
      params: { facilityId },
      search: next === "overview" ? {} : { tab: next },
      replace: true,
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !facility) {
    return (
      <div className="space-y-4 p-6">
        <Breadcrumbs items={[{ label: t("title"), to: "/facilities" }]} />
        <EmptyState
          icon={
            <svg
              className="text-danger"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          }
          title={t("errorTitle")}
          description={t("errorDescription")}
          actionLabel={t("backToList")}
          onAction={() => void navigate({ to: "/facilities" })}
        />
      </div>
    );
  }

  const deviceCount = summary.deviceCount;
  const associatedAccountCount = facility.accounts?.length ?? 0;

  const tabs: TabDef<FacilityTab>[] = [
    { value: "overview", label: t("facilities:detail.overview") },
    {
      value: "associations",
      label: t("facilities:detail.accountsContacts"),
      count: associatedAccountCount,
    },
    {
      value: "devices",
      label: t("cccp:backflowDevice.entity.plural"),
      count: deviceCount,
    },
    { value: "testing", label: t("facilities:detail.tabs.testing") },
    { value: "compliance", label: t("facilities:detail.tabs.compliance") },
    { value: "tasks", label: t("tasks:relatedPanel.title") },
    { value: "documents", label: t("documents:page.title") },
    { value: "history", label: t("facilities:detail.tabs.history") },
  ];

  return (
    <div className="space-y-4 p-6">
      <Breadcrumbs items={[{ label: t("title"), to: "/facilities" }, { label: facility.name }]} />

      <div className="space-y-4">
        <PageHeader
          title={
            <InlineEditable
              label={t("facilities:form.name")}
              disabled={!canUpdateFacility}
              display={<span>{facility.name}</span>}
            >
              {(close) => (
                <InlineTextEditor
                  ariaLabel={t("facilities:form.name")}
                  initial={facility.name}
                  close={close}
                  onSubmit={(v) => {
                    // Non-nullable on the wire: an emptied name is a no-op, not
                    // a clear.
                    if (v.trim() === "") return;
                    void patchField({ name: v.trim() });
                  }}
                />
              )}
            </InlineEditable>
          }
          meta={
            <FacilityMetaRow facility={facility} onDrillDevices={() => setActiveTab("devices")} />
          }
          actions={
            // Each action gates on its OWN grant: a compliance officer who may
            // write letters but not edit the premise still gets the letter door.
            <div className="flex gap-2">
              {can("violations", "create") && (
                <Button variant="secondary" onClick={() => setViolationDrawerOpen(true)}>
                  <EnforcementIcon className="size-4" />
                  {t("violations:create.action")}
                </Button>
              )}
              {can("notices", "create") && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    void navigate({ to: "/notices/new", search: { facility_id: facilityId } })
                  }
                >
                  <MailIcon className="size-4" />
                  {t("notices:letters.generate")}
                </Button>
              )}
              {canUpdateFacility && (
                <FacilityStatusControl
                  orgId={orgId}
                  facilityId={facilityId}
                  status={facility.status}
                />
              )}
            </div>
          }
        />

        <div>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            ariaLabel={t("title")}
          />

          <div
            className="mt-6"
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            {activeTab === "overview" && (
              // The Internal Notes card sits below the overview's field cards
              // (the same band the shared custom-fields card occupies).
              <div className="space-y-4">
                <FacilityOverviewTab
                  facility={facility}
                  facilityId={facilityId}
                  orgId={orgId}
                  canEdit={canUpdateFacility}
                  onPatch={patchField}
                  onOpenTab={setActiveTab}
                />
                <NotesCard parent="facility" parentId={facilityId} />
              </div>
            )}
            {activeTab === "associations" && (
              <div className="space-y-4">
                <FacilityAccountsCard
                  facilityId={facilityId}
                  accounts={facility.accounts ?? []}
                  isAdmin={canUpdateFacility}
                />
                {orgId && (
                  <ContactsCard orgId={orgId} facilityId={facilityId} isAdmin={canUpdateFacility} />
                )}
              </div>
            )}
            {activeTab === "devices" && (
              <DevicesTab facilityId={facilityId} canCreate={canCreateDevice} />
            )}
            {activeTab === "testing" && <FacilityTestingTab facilityId={facilityId} />}
            {activeTab === "compliance" && (
              <FacilityComplianceTab facilityId={facilityId} facility={facility} />
            )}
            {activeTab === "tasks" && <FacilityTasksTab facilityId={facilityId} />}
            {activeTab === "documents" && orgId && (
              <FacilityDocumentsTab facilityId={facilityId} orgId={orgId} />
            )}
            {activeTab === "history" && orgId && (
              <FacilityHistoryTab facilityId={facilityId} orgId={orgId} />
            )}
          </div>
        </div>
      </div>

      {violationDrawerOpen && (
        <CreateViolationDrawer
          open
          lockedFacilityId={facilityId}
          onClose={() => setViolationDrawerOpen(false)}
        />
      )}
    </div>
  );
}
